<script lang="ts" setup>
import CurrentImageProvider from '@/src/components/CurrentImageProvider.vue';
import { IMAGE_DRAG_MEDIA_TYPE } from '@/src/constants';
import { resolveSlotRendering } from '@/src/core/viewTypes';
import { useModelStore } from '@/src/store/datasets-models';
import { useViewStore } from '@/src/store/views';
import { computed, ref } from 'vue';

const props = defineProps<{ viewId: string }>();
const viewStore = useViewStore();
const modelStore = useModelStore();
// used to keep track of the dragenter/dragleave count
const dragCounter = ref(0);
const showDropTarget = computed(() => dragCounter.value > 0);

const rendering = computed(() =>
  resolveSlotRendering(viewStore.viewByID[props.viewId])
);

// A 3D view can still render meshes without an image; don't black it out.
const showNoDataOverlay = computed(() => {
  if (rendering.value.renderImageID) return false;
  const is3D = viewStore.viewByID[props.viewId]?.type === '3D';
  return !(is3D && modelStore.idList.length > 0);
});

const activeStyles = computed(() => {
  if (showDropTarget.value) {
    return {
      border: '2px dashed var(--pycad-primary, #ff9900)',
    };
  }
  if (viewStore.activeView && viewStore.activeView === props.viewId) {
    return {
      border: '2px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.45))',
    };
  }
  return {
    border: '2px solid var(--pycad-border, #282828)',
  };
});

function isValidDragEvent(event: DragEvent) {
  return event.dataTransfer?.types.includes(IMAGE_DRAG_MEDIA_TYPE);
}

function onDragEnter(event: DragEvent) {
  if (!isValidDragEvent(event)) return;
  dragCounter.value++;
  event.preventDefault();
}

function onDragLeave(event: DragEvent) {
  if (!isValidDragEvent(event)) return;
  event.preventDefault();
  dragCounter.value--;
}

function onDrop(event: DragEvent) {
  if (!isValidDragEvent(event)) return;
  event.preventDefault();
  dragCounter.value--;

  const droppedImageID = event.dataTransfer?.getData(IMAGE_DRAG_MEDIA_TYPE);
  if (droppedImageID) {
    viewStore.setActiveView(props.viewId);
    viewStore.setDataForView(props.viewId, droppedImageID);
  }
}
</script>

<template>
  <div
    class="grid-item"
    :style="activeStyles"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div v-show="showNoDataOverlay" class="overlay">
      <v-icon color="grey-darken-3" size="x-large">mdi-image-off</v-icon>
    </div>
    <CurrentImageProvider :image-id="rendering.renderImageID">
      <component :is="rendering.component" :view-id="viewId" />
    </CurrentImageProvider>
  </div>
</template>

<style scoped>
.grid-item {
  position: relative;
  box-sizing: border-box;
  border-radius: var(--pycad-radius, 10px);
  overflow: hidden;
  background: #000;
  transition: border-color 0.15s ease;
}

.overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  user-select: none;
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
